import React, { FC, useCallback, useEffect, useRef } from 'react';

import { noop } from '#/service/utils';

import { FormMarkdown } from '../../Form.styled';
import { getErrorMessagesIds } from '../../Form.utils';
import {
  Container,
  StyledTextArea,
  TextAreaContainer,
  TextAreaLabel,
} from '../FormInput.styled';
import { CommonInputTextProps } from '../FormInput.types';
import { ErrorMessage } from './ErrorMessage';

export const InputTextArea: FC<CommonInputTextProps> = ({
  dataKey,
  description,
  errorMessages,
  handleBlur = noop,
  handleFocus = noop,
  handleMouseEnter = noop,
  handleMouseLeave = noop,
  handleValidate = noop,
  inputValue,
  isFocused,
  label,
  required,
  updateInputValue = noop,
  touched,
  groupKey = '',
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const prevRequired = useRef<boolean>(!!required);

  const validate = useCallback(
    (value?: string) => {
      handleValidate(dataKey, groupKey, textAreaRef, value);
    },
    [dataKey, groupKey, handleValidate]
  );

  // re-validate when `required` changes
  useEffect(() => {
    if (!(touched && prevRequired.current === required)) {
      return;
    }
    textAreaRef.current?.setCustomValidity('');
    validate();
  }, [required, touched, validate]);

  const handleFocusWithRef = () => {
    handleFocus();
    textAreaRef.current?.focus();
  };

  const blurInput = () => {
    handleBlur();
    validate();
  };

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateInputValue(dataKey, { value });
    textAreaRef?.current?.setCustomValidity('');
    validate(value);
  };

  const handleFocusConditionally = () => {
    if (!isFocused) {
      handleFocusWithRef();
    }
  };

  useEffect(() => {
    if (errorMessages) {
      errorMessages.forEach((errorMessage) =>
        textAreaRef.current?.setCustomValidity(errorMessage)
      );
    }
  }, [errorMessages]);

  return (
    <Container>
      <TextAreaContainer
        className={inputValue && 'filled'}
        onClick={handleFocusConditionally}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <TextAreaLabel
          $hide={isFocused || !!inputValue}
          htmlFor={`${groupKey}-${dataKey}`}
        >
          {label}
        </TextAreaLabel>
        <StyledTextArea
          $touched={touched}
          aria-describedby={
            errorMessages?.length
              ? getErrorMessagesIds({
                  groupKey,
                  dataKey,
                  errorMessages,
                })
              : undefined
          }
          aria-invalid={!!errorMessages?.length}
          id={`${groupKey}-${dataKey}`}
          maxLength={1000}
          name={dataKey}
          onBlur={blurInput}
          onChange={handleChange}
          onFocus={handleFocusWithRef}
          ref={textAreaRef}
          required={true}
          value={inputValue ?? ''}
        />
      </TextAreaContainer>
      {description && <FormMarkdown>{description}</FormMarkdown>}
      {errorMessages?.map((errorMessage, index) => (
        <ErrorMessage
          id={`${groupKey}-${dataKey}-error-message-${index}`}
          key={`${groupKey}-${dataKey}-error-message-${index}`}
        >
          {errorMessage}
        </ErrorMessage>
      ))}
    </Container>
  );
};
